(() => {
  if (window.MinimaCartFlowV4) return;
  window.MinimaCartFlowV4 = true;

  const ROOT_SELECTOR = '[data-minima-cart-root]';
  const INPUT_SELECTOR = '.minima-cart__quantity-input[data-line-key]';
  const routeRoot = window.Shopify?.routes?.root || '/';
  const changeUrl = `${routeRoot}cart/change.js`;
  const inFlight = new Map();
  const inputTimers = new WeakMap();
  let pendingRequests = 0;

  const getRoot = (element = document) => element.closest?.(ROOT_SELECTOR) || element.querySelector?.(ROOT_SELECTOR);

  const getNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const getRules = (input) => ({
    min: Math.max(1, getNumber(input.dataset.ruleMin, 1)),
    max: Math.max(1, getNumber(input.max, 25)),
    step: Math.max(1, getNumber(input.step, 1)),
  });

  const normalizeQuantity = (input, rawValue = input.value) => {
    const { min, max, step } = getRules(input);
    let value = Math.trunc(getNumber(rawValue, min));

    if (value <= 0) return 0;
    value = Math.max(min, Math.min(value, max));

    const stepsFromMinimum = Math.round((value - min) / step);
    value = min + stepsFromMinimum * step;
    return Math.max(min, Math.min(value, max));
  };

  const setLineStatus = (line, message = '') => {
    const status = line?.querySelector('[data-line-status]');
    if (status) status.textContent = message;
  };

  const setError = (root, message = '') => {
    const error = root?.querySelector('[data-minima-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const setGlobalStatus = (root, message = '') => {
    const status = root?.querySelector('[data-minima-cart-status]');
    if (status) status.textContent = message;
  };

  const setPendingState = (root, line, pending) => {
    line?.classList.toggle('is-loading', pending);
    line?.querySelectorAll('button, input').forEach((control) => {
      control.disabled = pending;
    });

    root?.setAttribute('aria-busy', pendingRequests > 0 || pending ? 'true' : 'false');
    root?.querySelectorAll('[data-minima-checkout]').forEach((button) => {
      button.disabled = pendingRequests > 0 || pending;
    });
  };

  const updateQuantityButtons = (input) => {
    const control = input.closest('[data-minima-cart-quantity]');
    if (!control) return;

    const value = normalizeQuantity(input);
    const { max } = getRules(input);
    const increase = control.querySelector('[data-minima-cart-action="increase"]');
    if (increase) increase.disabled = value >= max;
  };

  const parseNewRoot = (html) => {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(ROOT_SELECTOR);
  };

  const findLineByKey = (root, key) => {
    return Array.from(root?.querySelectorAll('[data-cart-line]') || []).find(
      (line) => line.dataset.cartLineKey === key,
    );
  };

  const focusAfterRender = (root, key, focusTarget) => {
    const line = findLineByKey(root, key);
    if (!line) return;

    let target;
    if (focusTarget === 'increase' || focusTarget === 'decrease') {
      target = line.querySelector(`[data-minima-cart-action="${focusTarget}"]`);
    } else if (focusTarget === 'input') {
      target = line.querySelector(INPUT_SELECTOR);
    }

    target?.focus({ preventScroll: true });
  };

  const replaceCartSection = (root, cart, key, focusTarget) => {
    const sectionId = root.dataset.sectionId;
    const newRoot = parseNewRoot(cart.sections?.[sectionId]);

    if (!newRoot) {
      window.location.reload();
      return null;
    }

    root.replaceWith(newRoot);
    window.MinimaCartUI?.updateBadge(cart.item_count);
    document.dispatchEvent(new CustomEvent('minima:cart-updated', { detail: { cart } }));
    focusAfterRender(newRoot, key, focusTarget);
    normalizeInvalidQuantities(newRoot);
    return newRoot;
  };

  const requestLineChange = async ({ root, line, key, quantity, focusTarget = 'input', message }) => {
    const sectionId = root.dataset.sectionId;
    const previousController = inFlight.get(key);
    previousController?.abort();

    const controller = new AbortController();
    inFlight.set(key, controller);
    pendingRequests += 1;
    setError(root, '');
    setGlobalStatus(root, message || 'Actualizando la cesta…');
    setLineStatus(line, message || 'Guardando…');
    setPendingState(root, line, true);

    try {
      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: [sectionId],
          sections_url: window.location.pathname,
        }),
        signal: controller.signal,
      });

      const cart = await response.json();
      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'No se pudo actualizar la cesta.');
      }

      const productTitle = line?.dataset.cartLineTitle || 'Producto';
      const successMessage = quantity === 0
        ? `${productTitle} eliminado de la cesta.`
        : `Cantidad de ${productTitle} actualizada a ${quantity}.`;

      const newRoot = replaceCartSection(root, cart, key, focusTarget);
      window.MinimaCartUI?.announce(successMessage);
      if (newRoot) setGlobalStatus(newRoot, successMessage);
    } catch (error) {
      if (error.name === 'AbortError') return;

      const activeRoot = document.querySelector(ROOT_SELECTOR) || root;
      const currentLine = findLineByKey(activeRoot, key) || line;
      const input = currentLine?.querySelector(INPUT_SELECTOR);
      if (input) input.value = input.dataset.previousValue || input.value;

      const messageText = error.message || 'No se pudo actualizar la cesta. Inténtalo de nuevo.';
      setError(activeRoot, messageText);
      setLineStatus(currentLine, messageText);
      window.MinimaCartUI?.announce(messageText);
    } finally {
      if (inFlight.get(key) === controller) inFlight.delete(key);
      pendingRequests = Math.max(0, pendingRequests - 1);
      const activeRoot = document.querySelector(ROOT_SELECTOR) || root;
      const activeLine = findLineByKey(activeRoot, key);
      setPendingState(activeRoot, activeLine, false);
      if (pendingRequests === 0) activeRoot?.setAttribute('aria-busy', 'false');
    }
  };

  const scheduleLineChange = (input, delay = 450) => {
    const oldTimer = inputTimers.get(input);
    if (oldTimer) window.clearTimeout(oldTimer);

    const timer = window.setTimeout(() => {
      const root = getRoot(input);
      const line = input.closest('[data-cart-line]');
      const key = input.dataset.lineKey;
      if (!root || !line || !key) return;

      const quantity = normalizeQuantity(input);
      input.value = String(quantity);
      updateQuantityButtons(input);

      if (String(quantity) === input.dataset.previousValue) return;
      requestLineChange({ root, line, key, quantity, focusTarget: 'input' });
    }, delay);

    inputTimers.set(input, timer);
  };

  const normalizeInvalidQuantities = (root) => {
    if (!root || root.dataset.normalizing === 'true') return;
    const invalidInput = Array.from(root.querySelectorAll(INPUT_SELECTOR)).find((input) => {
      return getNumber(input.value, 0) !== normalizeQuantity(input);
    });

    if (!invalidInput) return;
    root.dataset.normalizing = 'true';

    const line = invalidInput.closest('[data-cart-line]');
    const key = invalidInput.dataset.lineKey;
    const quantity = normalizeQuantity(invalidInput);
    invalidInput.value = String(quantity);

    window.setTimeout(() => {
      requestLineChange({
        root,
        line,
        key,
        quantity,
        focusTarget: null,
        message: `Ajustando la cantidad al máximo permitido (${quantity})…`,
      });
    }, 150);
  };

  document.addEventListener('click', (event) => {
    const actionButton = event.target.closest('[data-minima-cart-action]');
    if (actionButton) {
      const root = getRoot(actionButton);
      const line = actionButton.closest('[data-cart-line]');
      const input = line?.querySelector(INPUT_SELECTOR);
      if (!root || !line || !input) return;

      event.preventDefault();
      const action = actionButton.dataset.minimaCartAction;
      const { min, max, step } = getRules(input);
      const current = normalizeQuantity(input);
      const quantity = action === 'increase'
        ? Math.min(max, current + step)
        : current <= min
          ? 0
          : Math.max(min, current - step);

      input.value = String(quantity);
      updateQuantityButtons(input);
      requestLineChange({
        root,
        line,
        key: input.dataset.lineKey,
        quantity,
        focusTarget: action,
      });
      return;
    }

    const removeButton = event.target.closest('[data-minima-cart-remove]');
    if (removeButton) {
      const root = getRoot(removeButton);
      const line = removeButton.closest('[data-cart-line]');
      const key = removeButton.dataset.lineKey;
      if (!root || !line || !key) return;

      event.preventDefault();
      requestLineChange({ root, line, key, quantity: 0, focusTarget: null, message: 'Eliminando producto…' });
    }
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest(INPUT_SELECTOR);
    if (!input) return;

    const raw = getNumber(input.value, 0);
    const { max } = getRules(input);
    const line = input.closest('[data-cart-line]');

    if (raw > max) {
      input.value = String(max);
      setLineStatus(line, `La cantidad máxima permitida es ${max}.`);
    } else if (raw < 0) {
      input.value = '0';
    } else {
      setLineStatus(line, '');
    }

    updateQuantityButtons(input);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest(INPUT_SELECTOR);
    if (!input) return;

    const normalized = normalizeQuantity(input);
    input.value = String(normalized);
    updateQuantityButtons(input);
    scheduleLineChange(input);
  });

  document.addEventListener('keydown', (event) => {
    const input = event.target.closest(INPUT_SELECTOR);
    if (!input || event.key !== 'Enter') return;

    event.preventDefault();
    const timer = inputTimers.get(input);
    if (timer) window.clearTimeout(timer);
    scheduleLineChange(input, 0);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-minima-cart-form]');
    if (!form) return;

    const submitter = event.submitter;
    if (submitter?.name !== 'checkout') return;

    form.querySelectorAll(INPUT_SELECTOR).forEach((input) => {
      const timer = inputTimers.get(input);
      if (timer) window.clearTimeout(timer);
      input.value = String(normalizeQuantity(input));
    });

    const root = getRoot(form);
    root?.classList.add('is-checking-out');
    setGlobalStatus(root, 'Abriendo el pago seguro…');
    window.MinimaCartUI?.announce('Abriendo el pago seguro…');
  });

  document.addEventListener('change', (event) => {
    const note = event.target.closest('[data-minima-cart-note]');
    if (!note) return;

    fetch(`${routeRoot}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ note: note.value }),
    }).catch(() => {
      const root = getRoot(note);
      setError(root, 'La nota se guardará al finalizar el pedido.');
    });
  });

  const initialize = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    root?.querySelectorAll(INPUT_SELECTOR).forEach(updateQuantityButtons);
    normalizeInvalidQuantities(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
